import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Truck, MapPin, Phone, Calendar } from 'lucide-react';
import { Button } from './ui/button';
import { useTheme } from '../contexts/ThemeContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERIODS = [
  { value: 7, label: '7d' },
  { value: 30, label: '30d' },
  { value: 90, label: '90d' },
];

export const TopDeliveryCustomers = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { theme } = useTheme();
  const isLight = theme === 'claro';

  // Paleta adaptativa (mesmo padrão do DashboardPage)
  const C = isLight
    ? {
        cardBg: '#FFFFFF',
        cardBorder: 'rgba(15, 23, 42, 0.08)',
        muted: '#64748B',
        strong: '#0F172A',
        rowBg: '#F8FAFC',
        rowBorder: 'rgba(15, 23, 42, 0.06)',
        badgeBg: '#E2E8F0',
        badgeText: '#334155',
        placeholder: '#64748B',
        outlineBtn: 'bg-white border-slate-300 text-slate-700 hover:bg-slate-100',
        shadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
      }
    : {
        cardBg: '#1E2640',
        cardBorder: 'rgba(148, 163, 184, 0.12)',
        muted: '#94A3B8',
        strong: '#FFFFFF',
        rowBg: 'rgba(15, 23, 42, 0.4)',
        rowBorder: 'rgba(148, 163, 184, 0.1)',
        badgeBg: '#334155',
        badgeText: '#CBD5E1',
        placeholder: '#94A3B8',
        outlineBtn: 'bg-transparent border-slate-600 text-slate-300 hover:bg-slate-700',
        shadow: 'none',
      };

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/dashboard/top-delivery-customers?days=${days}&limit=10`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [days]);

  return (
    <div className="rounded-md p-5" style={{ background: C.cardBg, border: `1px solid ${C.cardBorder}`, boxShadow: C.shadow }} data-testid="top-delivery-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: 'rgba(249, 115, 22, 0.15)' }}>
            <Truck className="text-orange-500" size={18} />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color: C.muted }}>Top 10 Delivery</p>
            <h3 className="text-base font-bold" style={{ color: C.strong }}>Clientes que Mais Compram por Entrega</h3>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {PERIODS.map((p) => (
            <Button
              key={p.value}
              size="sm"
              variant={days === p.value ? 'default' : 'outline'}
              onClick={() => setDays(p.value)}
              className={`h-7 px-2 text-xs ${days === p.value ? 'bg-orange-600 hover:bg-orange-700 text-white border-orange-600' : C.outlineBtn}`}
              data-testid={`top-delivery-period-${p.value}`}
            >
              {p.label}
            </Button>
          ))}
        </div>
      </div>

      {loading ? (
        <p className="text-center p-6 text-sm" style={{ color: C.placeholder }}>Carregando...</p>
      ) : !data ? (
        <p className="text-center p-6 text-sm" style={{ color: C.placeholder }}>Sem dados disponíveis</p>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-3 gap-2 mb-3">
            <div className="rounded p-2 text-center border" style={{ background: 'rgba(249, 115, 22, 0.12)', borderColor: 'rgba(249, 115, 22, 0.35)' }}>
              <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: isLight ? '#C2410C' : '#FDBA74' }}>Faturamento</p>
              <p className="text-lg font-black font-mono" style={{ color: isLight ? '#C2410C' : '#FDBA74' }}>R$ {data.summary.total_delivery.toFixed(2)}</p>
            </div>
            <div className="rounded p-2 text-center border" style={{ background: 'rgba(59, 130, 246, 0.12)', borderColor: 'rgba(59, 130, 246, 0.35)' }}>
              <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: isLight ? '#1D4ED8' : '#93C5FD' }}>Entregas</p>
              <p className="text-lg font-black font-mono" style={{ color: isLight ? '#1D4ED8' : '#93C5FD' }}>{data.summary.total_orders}</p>
            </div>
            <div className="rounded p-2 text-center border" style={{ background: 'rgba(16, 185, 129, 0.12)', borderColor: 'rgba(16, 185, 129, 0.35)' }}>
              <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: isLight ? '#047857' : '#6EE7B7' }}>Clientes</p>
              <p className="text-lg font-black font-mono" style={{ color: isLight ? '#047857' : '#6EE7B7' }}>{data.summary.unique_customers}</p>
            </div>
          </div>

          {/* Lista */}
          {data.top.length === 0 ? (
            <p className="text-center p-6 text-sm" style={{ color: C.placeholder }}>Nenhuma entrega registrada no período</p>
          ) : (
            <div className="space-y-1.5 max-h-72 overflow-y-auto">
              {data.top.map((c, i) => (
                <div
                  key={c.phone}
                  className="flex items-center gap-2 p-2 rounded border transition-colors"
                  style={{ background: C.rowBg, borderColor: C.rowBorder }}
                  data-testid={`top-delivery-row-${i}`}
                >
                  <span className={`w-7 h-7 flex items-center justify-center rounded-full font-black text-xs flex-shrink-0 ${
                    i === 0 ? 'bg-amber-400 text-slate-900' :
                    i === 1 ? 'bg-slate-300 text-slate-900' :
                    i === 2 ? 'bg-orange-400 text-slate-900' :
                    (isLight ? 'bg-slate-200 text-slate-700' : 'bg-slate-700 text-slate-300')
                  }`}>{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-bold truncate" style={{ color: C.strong }}>{c.name}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: C.badgeBg, color: C.badgeText }}>
                        {c.count}x · ticket R$ {c.ticket_medio.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-[10px]" style={{ color: C.muted }}>
                      <Phone size={9} /> {c.phone}
                      {c.address && <><MapPin size={9} className="ml-1" /> {c.address}{c.neighborhood ? ` · ${c.neighborhood}` : ''}</>}
                    </div>
                    {c.last_order_at && (
                      <div className="flex items-center gap-1 text-[10px]" style={{ color: C.muted }}>
                        <Calendar size={9} /> Última: {new Date(c.last_order_at).toLocaleDateString('pt-BR')}
                      </div>
                    )}
                  </div>
                  <span className="text-base font-black whitespace-nowrap font-mono" style={{ color: isLight ? '#EA580C' : '#FB923C' }}>R$ {c.total.toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TopDeliveryCustomers;
